<template>
  <BoxWithTitle
    collapsible
    openingIcon="mdi-chevron-down"
    closingIcon="mdi-chevron-up"
  >
    <template #header>
      <v-toolbar-title>
        {{ $t('standards.legionellaStandards') }}
      </v-toolbar-title>
    </template>
    <template #content>
      <v-skeleton-loader
        v-if="loading"
        class="m-auto"
        type="table"
        transition="scale-transition"
      />
      <VTableLocal
        v-else-if="hasPermission('standards.view') && hasPermission('standardsFamilies.view') && hasPermission('analyses.types.view')"
        :iconHeader="hasPermission('standards.add')? 'add' : null"
        :items="displayedStandards"
        isSearchable
        :headers="tableHeaders"
        :itemActions="actions"
        :title="$t('standards.legionellaStandards')"
        @iconAction="onClickAction"
        @click-icon-header="onClickAdd"
      />
    </template>
  </BoxWithTitle>
</template>

<script>
import { cloneDeep } from 'lodash';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import LegionellaStandardForm from '@/components/forms/LegionellaStandardForm.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import VTableLocal from '@/components/shared/content-views/VTableLocal';
import analysisTypesConstant from '@/aquassist-shared/constants/types/componentAnalysisConfiguration';

export default {
    components: {
        VTableLocal,
        BoxWithTitle,
    },
    data() {
        return {
            standards: [],
            loading: false,
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'hasPermission',
        ]),
        tableHeaders() {
            return [
                'networkType',
                'componentType',
                'analysisType',
                'normal',
                'intervention',
                'risk',
            ].map(header => ({
                text: this.$t(`standards.labels.${header}`),
                value: header,
            }));
        },
        actions() {
            const actions = [];
            if (this.hasPermission('standards.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('standards.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
        displayedStandards() {
            return this.standards
                .filter(standard => standard.analysisType !== undefined && standard.analysisType?.type === analysisTypesConstant.TYPE_LEGIONELLA)
                .map(standard => {
                    const { networkType, componentType, analysisType, norm } = standard;
                    const { intervention, risk } = norm;
                    return {
                        ...standard,
                        networkType: networkType ? networkType.name : this.$t('forms.labels.any'),
                        componentType: componentType ? componentType.name : this.$t('forms.labels.any'),
                        analysisType: `${analysisType.name[this.userLang]} - (${analysisType.measureUnit[this.userLang]})`,
                        intervention,
                        risk,
                        normal: 0,
                    };
                });
        },
        analysisTypes() {
            return this.standards.map(({ analysisType }) => analysisType.id);
        },
    },
    async created() {
        await this.getStandards();
    },
    methods: {
        async getStandards() {
            this.loading = true;
            const queryParams = { standardFamily: this.$route.params.standardFamilyId };
            const { data } = await this.$rq.getStandards(queryParams);
            this.standards = data.standards;
            this.loading = false;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('standards.addLegionellaStandard'),
                component: Vue.component('LegionellaStandardForm', LegionellaStandardForm),
                payload: {
                    standards: this.standards,
                },
                onConfirm: requestPayload => this.addStandardRequest(requestPayload),
            });
        },
        async onClickAction(item, action) {
            if (action.mode === 'edit') {
                const standard = this.standards.find(({ id }) => id === item.id);

                this.$root.$emit('showModal', {
                    title: this.$t('standards.editStandard'),
                    component: Vue.component('LegionellaStandardForm', LegionellaStandardForm),
                    validateAtInitialization: true,
                    payload: {
                        standard,
                        isEdition: true,
                        standards: this.standards,
                    },
                    onConfirm: requestPayload => this.updateStandardRequest(item.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('standards.removeStandard'),
                    component: Vue.component('confirmRemoveStandardForm', ConfirmForm),
                    payload: { msg: this.$t('standards.confirmRemoveStandard') },
                    onConfirm: () => this.deleteStandardRequest(item.id),
                    isForm: false,
                });
            }
        },
        preparePayload(payload) {
            const requestPayload = cloneDeep(payload);
            if (requestPayload.networkType === null) {
                delete requestPayload.networkType;
            }
            if (requestPayload.componentType === null) {
                delete requestPayload.componentType;
            }
            return requestPayload;
        },
        async addStandardRequest(payload) {
            const requestPayload = this.preparePayload(payload);
            requestPayload.standardFamily = this.$route.params.standardFamilyId;

            await this.$rq.addStandard(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.standardCreated'),
            });
            await this.getStandards();
        },
        async updateStandardRequest(standardId, payload) {
            const requestPayload = this.preparePayload(payload);
            await this.$rq.updateStandard(standardId, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.standardModified'),
            });
            await this.getStandards();
        },
        async deleteStandardRequest(standardId) {
            await this.$rq.deleteStandard(standardId);
            this.$toasted.global.toast_success({
                message: this.$t('msg.standardRemoved'),
            });
            await this.getStandards();
        },
    },
};
</script>
